import { useEffect, useRef } from 'react'

// React's onWheel is passive, so preventDefault() is a no-op there — attach a
// native non-passive listener instead so vertical wheel can be consumed and
// re-applied as horizontal scroll. Walks up to the nearest scrollable ancestor
// in case the bound element itself has overflow: visible (e.g. EstimatorAvatarSelect
// in map mode, where the outer toolbar slot is the actual scroll container).
export function useHorizontalWheelScroll<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return

    const findScrollTarget = (): HTMLElement | null => {
      let node: HTMLElement | null = el
      while (node) {
        if (node.scrollWidth - node.clientWidth > 1) {
          const overflowX = getComputedStyle(node).overflowX
          if (overflowX === 'auto' || overflowX === 'scroll') return node
        }
        node = node.parentElement
      }
      return null
    }

    const onWheel = (e: WheelEvent) => {
      if (e.deltaY === 0 || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return
      const target = findScrollTarget()
      if (!target) return
      e.preventDefault()
      target.scrollLeft += e.deltaY
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [])
  return ref
}
